
(() => {
  if (window.__KamokProductRecommendationsCompactV5) return;
  window.__KamokProductRecommendationsCompactV5 = true;

  const initialized = new WeakSet();
  const expanded = new WeakSet();

  const rootUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>'"]/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[c]));

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'lt-LT',
        { style: 'currency', currency }
      ).format(value);
    } catch {
      return value.toFixed(2) + ' €';
    }
  };

  const imageUrl = (product) => {
    const image =
      product?.featured_image?.url ||
      product?.featured_image ||
      product?.image ||
      product?.images?.[0];

    return typeof image === 'string'
      ? image
      : image?.src || image?.url || '';
  };

  const normalize = (product) => {
    const variants = Array.isArray(product?.variants)
      ? product.variants
      : [];

    const variant =
      variants.find((item) => item.available !== false) ||
      variants[0];

    if (!variant) return null;

    const price = Number(
      variant.price ??
      product.price_min ??
      product.price ??
      0
    );

    if (!price) return null;

    return {
      id: String(product.id || ''),
      title: String(product.title || ''),
      url: product.url || '/products/' + (product.handle || ''),
      image: imageUrl(product),
      price,
      available:
        product.available !== false &&
        variant.available !== false
    };
  };

  const brainCard = (item) => {
    const image = item.image
      ? '<img src="' + escapeHtml(item.image) +
        '" alt="" loading="lazy" width="320" height="320">'
      : '';

    return (
      '<article class="kamok-brain-related__card">' +
        '<a class="kamok-brain-related__media" href="' +
          escapeHtml(item.url) + '">' +
          image +
        '</a>' +
        '<div class="kamok-brain-related__copy">' +
          '<a class="kamok-brain-related__title" href="' +
            escapeHtml(item.url) + '">' +
            escapeHtml(item.title) +
          '</a>' +
          '<div class="kamok-brain-related__price">' +
            money(item.price) +
          '</div>' +
        '</div>' +
      '</article>'
    );
  };

  async function expandBrain(root) {
    if (expanded.has(root)) return;
    expanded.add(root);

    const productId = String(root.dataset.productId || '');
    const list = root.querySelector(
      '[data-kamok-brain-related-list]'
    );

    if (!productId || !list) return;

    try {
      const url =
        rootUrl() +
        'recommendations/products.json?product_id=' +
        encodeURIComponent(productId) +
        '&limit=10&intent=related';

      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });

      if (!response.ok) return;

      const payload = await response.json();
      const seen = new Set();

      const items = (payload.products || [])
        .map(normalize)
        .filter(Boolean)
        .filter((item) => {
          if (
            !item.available ||
            item.id === productId ||
            seen.has(item.id)
          ) {
            return false;
          }

          seen.add(item.id);
          return true;
        })
        .slice(0, 5);

      if (items.length) {
        list.innerHTML = items.map(brainCard).join('');
        root.hidden = false;
      }
    } catch (error) {
      console.warn(
        '[KamOk compact recommendations] related expansion failed',
        error
      );
    }
  }

  function makeNav(root, rail) {
    if (root.querySelector(':scope > .kamok-compact-rec__nav')) {
      return root.querySelector(
        ':scope > .kamok-compact-rec__nav'
      );
    }

    const nav = document.createElement('div');
    nav.className = 'kamok-compact-rec__nav';

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className =
      'kamok-compact-rec__button kamok-compact-rec__button--prev';
    prev.setAttribute('aria-label', 'Ankstesnės prekės');
    prev.textContent = '‹';

    const next = document.createElement('button');
    next.type = 'button';
    next.className =
      'kamok-compact-rec__button kamok-compact-rec__button--next';
    next.setAttribute('aria-label', 'Kitos prekės');
    next.textContent = '›';

    nav.append(prev, next);
    root.append(nav);

    const step = () =>
      Math.max(160, Math.round(rail.clientWidth * 0.78));

    prev.addEventListener('click', () => {
      rail.scrollBy({ left: -step(), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      rail.scrollBy({ left: step(), behavior: 'smooth' });
    });

    return nav;
  }

  function setupCarousel(root, rail) {
    if (!root || !rail || initialized.has(rail)) return;
    initialized.add(rail);

    root.classList.add('kamok-compact-rec');
    rail.classList.add('kamok-compact-rec__rail');

    const nav = makeNav(root, rail);
    const prev = nav.querySelector(
      '.kamok-compact-rec__button--prev'
    );
    const next = nav.querySelector(
      '.kamok-compact-rec__button--next'
    );

    const update = () => {
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const desktop = window.matchMedia('(min-width: 750px)').matches;
      const overflow = max > 4;

      nav.hidden = !desktop || !overflow;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= max - 2;
    };

    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(rail);
    }

    requestAnimationFrame(update);
    setTimeout(update, 350);
  }

  async function initBrain(root) {
    if (!root) return;

    if (root.dataset.ready !== '1') {
      setTimeout(() => initBrain(root), 180);
      return;
    }

    await expandBrain(root);

    const rail = root.querySelector(
      '[data-kamok-brain-related-list]'
    );

    setupCarousel(root, rail);
  }

  function initTogether(root) {
    if (!root || root.dataset.context !== 'product') return;

    if (root.dataset.ready !== '1') {
      setTimeout(() => initTogether(root), 180);
      return;
    }

    const rail = root.querySelector('[data-kamok-depo-list]');
    setupCarousel(root, rail);
  }

  function scan(scope = document) {
    if (
      scope.matches?.(
        '[data-kamok-brain-related-embed]'
      )
    ) {
      initBrain(scope);
    }

    scope.querySelectorAll?.(
      '[data-kamok-brain-related-embed]'
    ).forEach(initBrain);

    if (
      scope.matches?.(
        '[data-kamok-depo][data-context="product"]'
      )
    ) {
      initTogether(scope);
    }

    scope.querySelectorAll?.(
      '[data-kamok-depo][data-context="product"]'
    ).forEach(initTogether);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => scan(),
      { once: true }
    );
  } else {
    scan();
  }

  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1) scan(node);
      }
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  document.addEventListener(
    'shopify:section:load',
    (event) => scan(event.target)
  );
})();
